import {
    type CreateExtArgs,
    type CreateExtensionArgument,
    type DalJsStore,
    type DeepFunctionMap,
    type DmApis,
    type Extension,
    type ExtensionAPI,
    pointerUtils,
    type SnapshotDal
} from '@wix/document-manager-core'
import type {
    BuilderStyle,
    CompRef,
    ConversionAlgoConfigOverrides,
    Pointer,
    ResolvedFunctions,
    VariantPointer
} from '@wix/document-services-types'
import type {
    AppliedHeuristicLogMessage,
    BaseStageData,
    ConversionAlgoConfig,
    ConversionDal,
    MobileAlgoContext,
    MobileAlgoContextInternalStore,
    MobileAlgoPluginFactory,
    MobileAlgoPluginInitializationArgs,
    MobileAlgoPluginWithContext,
    MobileAlgoRunFunction,
    PluginStageHandler,
    ReadOnlyExtensionAPI,
    Stage,
    Stages,
    StructureIterators
} from './types'
import {AlgorithmModes, ChildrenProperties, DomOrderStrategy, NaryTreeTraversalOrder, StageName} from './enums'
import _ from 'lodash'
import * as plugins from './plugins'
import {createStructureStage} from './stages/structureStage'
import {createComponentStage} from './stages/componentStage'
import {createPluginContextHelper} from './pluginLifeCycle/pluginContextHelper'
import {createPluginHeuristicsRegistry} from './pluginLifeCycle/pluginHeuristicsRegistry'
import {createPluginContext} from './pluginLifeCycle/pluginContext'
import {createConversionDal} from './conversionDal/conversionDal'
import {createReadOnlyExtensionApi} from './readOnlyExtensionApi'
import type {VariantsExtensionAPI} from '../variants/variants'
import type {GroupApi} from './plugins/grouping/types'
import {sendRequest} from './plugins/grouping/request'
import {DATA_TYPES, VARIANTS, VIEW_MODES, COMP_TYPES} from '../../constants/constants'
import {conversionDataBuilders} from './preprocess'
import {mobileConversionValidation} from './validation'
import {createStructureIterators} from './conversionDal/utils/structureIterators'
import {ConversionDalNamespaces} from './conversionDal/constants'
import {
    getInnerElementPointer,
    getStructurePointer,
    getStylePointer,
    getTargetLayoutPointer,
    getTransformationsPointer
} from './conversionDal/utils/pointer'
import type {PageExtensionAPI} from '../page'
import {getMobileBreakpoint} from './preprocess/utils/mobileBreakPoint'
import type {MeasurementsExtensionAPI} from '../measurements/measurements'
import type {LayoutExtensionApi} from '../layout/layout'
import type {InnerElementsExtensionAPI} from '../builderComponents/innerElements'
import {StageRunner} from './stages/stageRunner'
import {getFontSize} from './plugins/textScale/utils'
import {deepClone} from '@wix/wix-immutable-proxy'
import {MOBILE_ALGO_VERSION} from './config'
import type {BuilderStyleExtensionAPI} from '../builderComponents/builderStyle'
import type {BuilderComponentsExtensionAPI} from '../builderComponents/builderComponents'
import type {StructureExtensionAPI} from '../structure'
import * as conversionDataTransformers from './postprocess/conversionDataTransformers'

export const DOCUMENT_STAGE_WIDTH = 1280
export const MOBILE_VARIANT = {type: 'variants', id: VARIANTS.MOBILE_VARIANT_ID}

export interface MobileAlgoApi extends ExtensionAPI {
    locking: {
        isComponentLocked(compRef: CompRef): boolean
        unlockSectionsForPage(pagePointer: CompRef): void
        unlockSectionsForAllPages(): void
    }
    context: {
        enable(ctx: MobileAlgoContext, plugins?: string[]): void
        disable(ctx: MobileAlgoContext, plugins?: string[]): void
        create(enabledPlugins?: string[]): MobileAlgoContext
    }
    plugins: {
        register(pluginFactory: MobileAlgoPluginFactory): void
        getApis(ctx: MobileAlgoContext): DeepFunctionMap
    }
    utils: {
        getFontSize(fontCssString: string): {
            targetFontSize?: number
            unit?: string
        }
    }
    heuristics: HeuristicRegistry
    createConversionConfig(
        containerPointer: CompRef,
        configurationOverrides?: ConversionAlgoConfigOverrides
    ): ConversionAlgoConfig
    writeConversionResult(
        conversionDal: ConversionDal,
        initialSnapshot: SnapshotDal,
        structureIterators: StructureIterators,
        conversionAlgoConfig: ConversionAlgoConfig,
        ctx: MobileAlgoContext
    ): void
    runWithContext(
        containerPointer: CompRef,
        ctx: MobileAlgoContext,
        config: ConversionAlgoConfig
    ): Promise<{
        conversionDal: ConversionDal
        initialSnapshot: SnapshotDal
        structureIterators: StructureIterators
    }>
    run(containerPointer: CompRef, config?: ConversionAlgoConfigOverrides): Promise<void>
    runForAllPages(): Promise<void[]>
    runForPage(pagePointer: CompRef, config?: ConversionAlgoConfigOverrides): Promise<void[]>
}

export interface HeuristicRegistry extends ExtensionAPI {
    registerHeuristic<T extends BaseStageData>(stage: Stage<T>, handler: PluginStageHandler<T>): void
}

export interface MobileAlgoExtensionApi extends ExtensionAPI {
    mobileAlgo: MobileAlgoApi
}

const createExtension = ({environmentContext, experimentInstance, logger}: CreateExtensionArgument): Extension => {
    const bootPlugins: MobileAlgoPluginFactory[] = Object.values(plugins)

    const createExtensionAPI = ({pointers, dal, extensionAPI}: CreateExtArgs): MobileAlgoExtensionApi => {
        const variantsApi = () => (extensionAPI as VariantsExtensionAPI).variants
        const pages = () => (extensionAPI as PageExtensionAPI).page

        const readOnlyExtensionAPI: ReadOnlyExtensionAPI = createReadOnlyExtensionApi(extensionAPI)
        const stages: Stages = {
            [StageName.COMPONENT_CATEGORIZATION]: createComponentStage(),
            [StageName.GROUPING]: createStructureStage(),
            [StageName.GROUPING_ORDER]: createComponentStage({
                structuralType: ConversionDalNamespaces.virtualStructure
            }),
            [StageName.INIT_CSS_MODELS]: createComponentStage(),
            [StageName.STRUCTURE_ORDER]: createComponentStage(),
            [StageName.HORIZONTAL_LAYOUT_SCALE]: createComponentStage(),
            [StageName.VERTICAL_LAYOUT_SCALE]: createComponentStage({
                traversalOrder: NaryTreeTraversalOrder.PostOrder
            }),
            [StageName.TRANSFORM_STYLE]: createComponentStage(),
            [StageName.POST_PROCESS]: createComponentStage()
        }
        const actionsLogger: AppliedHeuristicLogMessage[] = []
        const pluginRegistry = new Map<string, MobileAlgoPluginWithContext>()

        const getPageId = (containerPointer: CompRef) => pointers.structure.getPageOfComponent(containerPointer).id

        const filterPlugins = (pluginsToEnable?: string[]): MobileAlgoPluginWithContext[] => {
            if (!pluginsToEnable) {
                return Array.from(pluginRegistry.values())
            }

            return Array.from(pluginRegistry.values()).filter(plugin => pluginsToEnable.includes(plugin.name))
        }

        const disablePluginsForContext = (ctx: MobileAlgoContext, pluginsToEnable?: string[]): void => {
            filterPlugins(pluginsToEnable).forEach(plugin => {
                plugin.contextHelper.disable(ctx)
            })
        }

        const enablePluginsForContext = (ctx: MobileAlgoContext, pluginsToEnable?: string[]) => {
            filterPlugins(pluginsToEnable).forEach(plugin => {
                plugin.contextHelper.enable(ctx)

                if (plugin.dependencies) {
                    enablePluginsForContext(ctx, plugin.dependencies as any)
                }
            })
        }

        const getPluginApis = (ctx: MobileAlgoContext): DeepFunctionMap => {
            const result: DeepFunctionMap = {}

            Array.from(pluginRegistry.values()).forEach((plugin: MobileAlgoPluginWithContext) => {
                if (plugin.createApi) {
                    const pluginContext = createPluginContext(plugin.contextHelper, ctx)
                    result[plugin.name] = plugin.createApi(pluginContext)
                }
            })

            return result
        }

        const registerRequest = (ctx: MobileAlgoContext) => {
            const apis = getPluginApis(ctx)
            const {grouping} = apis as GroupApi
            grouping.registerServer(sendRequest)
        }

        const createContextStore = () => {
            const internalStore: MobileAlgoContextInternalStore = {}
            return {
                get: (key: string) => {
                    return internalStore[key]
                },
                set: (key: string, value: any) => {
                    internalStore[key] = value
                }
            }
        }

        const createContext = (enabledPlugins?: string[]): MobileAlgoContext => {
            const ctx: MobileAlgoContext = createContextStore()

            if (enabledPlugins) {
                disablePluginsForContext(ctx)
                enablePluginsForContext(ctx, enabledPlugins)
            } else {
                enablePluginsForContext(ctx)
            }

            registerRequest(ctx)

            return ctx
        }

        const registerPlugin = (pluginFactory: MobileAlgoPluginFactory) => {
            const algoApi = (extensionAPI as MobileAlgoExtensionApi).mobileAlgo
            const initializeArgs: MobileAlgoPluginInitializationArgs = {
                experimentInstance,
                readOnlyExtensionAPI,
                stages,
                environmentContext,
                logger
            }
            const plugin = pluginFactory.createPlugin(initializeArgs)
            const contextHelper = createPluginContextHelper(plugin.name)
            const pluginWithContext = {...plugin, contextHelper}
            const heuristicsRegistry = createPluginHeuristicsRegistry(algoApi.heuristics, contextHelper)
            pluginRegistry.set(plugin.name, pluginWithContext)
            plugin.register(heuristicsRegistry)
        }

        const registerHeuristic = <T extends BaseStageData>(stage: Stage<T>, handler: PluginStageHandler<T>) => {
            stage.register(handler)
        }

        interface IdAndDepth {
            id: string
            depth: number
        }

        const setFallbackMeasurements = (containerPointer: CompRef, initialStore: DalJsStore) => {
            const fallBackMeasurements = (extensionAPI as MeasurementsExtensionAPI).measurements.generateMeasurement(
                containerPointer
            )

            const queue: IdAndDepth[] = [{id: containerPointer.id, depth: 0}]

            while (queue.length > 0) {
                const {id, depth} = queue.shift()!
                const compPointer = pointerUtils.getPointerFromPointer(id, containerPointer) as CompRef
                const component = dal.get(compPointer)

                initialStore[ConversionDalNamespaces.measurements][id] = fallBackMeasurements[id]
                initialStore[ConversionDalNamespaces.structure][id] = {
                    ...initialStore[ConversionDalNamespaces.structure][id],
                    axisAlignedEnvelope: conversionDataBuilders.createEnvelope(fallBackMeasurements[id], 0)
                }

                for (const childId of component.components ?? []) {
                    queue.push({id: childId, depth: depth + 1})
                }
            }
        }

        const prepareConversionData = (
            containerPointer: CompRef,
            conversionAlgoConfig: ConversionAlgoConfig
        ): DalJsStore => {
            let useFallBack = false

            const queue: IdAndDepth[] = [{id: containerPointer.id, depth: 0}]
            const initialStore: DalJsStore = {
                [ConversionDalNamespaces.structure]: {},
                [ConversionDalNamespaces.measurements]: {},
                [ConversionDalNamespaces.sourceLayout]: {},
                [ConversionDalNamespaces.targetLayout]: {},
                [ConversionDalNamespaces.style]: {},
                [ConversionDalNamespaces.transformations]: {},
                [ConversionDalNamespaces.innerElements]: {}
            }
            const shouldValidateComponentLayout = experimentInstance.isOpen(
                'dm_shouldValidateComponentLayoutMobileAlgo'
            )

            while (queue.length > 0) {
                const {id, depth} = queue.shift()!
                const compPointer = pointerUtils.getPointerFromPointer(id, containerPointer) as CompRef
                const component = dal.get(compPointer)
                const parentComponent = dal.get({id: component.parent, type: VIEW_MODES.DESKTOP})

                if (shouldValidateComponentLayout) {
                    mobileConversionValidation(component, parentComponent, containerPointer)
                }

                const conversionData = conversionDataBuilders.getConversionData(
                    dal,
                    extensionAPI,
                    pointers,
                    compPointer,
                    conversionAlgoConfig
                )

                for (const [namespace, value] of Object.entries(conversionData)) {
                    _.merge(initialStore[namespace], value)
                }

                if (_.isEmpty(initialStore[ConversionDalNamespaces.measurements][id])) {
                    useFallBack = true
                }

                for (const childId of component.components ?? []) {
                    queue.push({id: childId, depth: depth + 1})
                }
            }

            if (useFallBack) {
                setFallbackMeasurements(containerPointer, initialStore)
            }

            return initialStore
        }

        const setAutoResponsivenessSettings = (
            conversionAlgoConfig: ConversionAlgoConfig,
            keyToUpdate: string,
            valueToUpdate: string
        ) => {
            const layoutSettings = variantsApi().getComponentItemConsideringVariants(
                conversionAlgoConfig.rootPointer,
                DATA_TYPES.layoutSettings
            )
            variantsApi().updateComponentDataConsideringVariants(
                conversionAlgoConfig.rootPointer,
                {
                    ...layoutSettings,
                    autoResponsivenessSettings: {
                        ...layoutSettings?.autoResponsivenessSettings,
                        [keyToUpdate]: valueToUpdate
                    },
                    type: 'LayoutSettings'
                },
                DATA_TYPES.layoutSettings
            )
        }

        const setLastGroupingHash = (conversionAlgoConfig: ConversionAlgoConfig, lastGroupingHash: string) =>
            setAutoResponsivenessSettings(conversionAlgoConfig, 'lastGroupingsHash', lastGroupingHash)

        const setMobileAlgoVersion = (conversionAlgoConfig: ConversionAlgoConfig, mobileAlgoVersion: string) =>
            setAutoResponsivenessSettings(conversionAlgoConfig, 'lastMobileAlgoVersion', mobileAlgoVersion)

        const writeConversionResult = (
            conversionDal: ConversionDal,
            initialSnapshot: SnapshotDal,
            structureIterators: StructureIterators,
            conversionAlgoConfig: ConversionAlgoConfig,
            ctx: MobileAlgoContext
        ): void => {
            const {builderComponents} = extensionAPI as BuilderComponentsExtensionAPI
            const {components} = extensionAPI as StructureExtensionAPI
            const {builderStyle} = extensionAPI as BuilderStyleExtensionAPI

            const updateLayoutSettings = () => {
                const apis = getPluginApis(ctx)
                const {grouping} = apis as GroupApi
                const groupingHashResult = grouping.getAiGroupTree()?.md5

                if (groupingHashResult && groupingHashResult !== conversionAlgoConfig.lastGroupingHash) {
                    setLastGroupingHash(conversionAlgoConfig, groupingHashResult)
                }

                if (conversionAlgoConfig.lastMobileAlgoVersion !== conversionAlgoConfig.MOBILE_ALGO_VERSION) {
                    setMobileAlgoVersion(conversionAlgoConfig, conversionAlgoConfig.MOBILE_ALGO_VERSION)
                }
            }

            const updateResponsiveLayout = (compPointer: CompRef, changes: DalJsStore) => {
                if (!changes[ConversionDalNamespaces.targetLayout]?.[compPointer.id]) {
                    return
                }

                const layoutToUpdate = deepClone(conversionDal.get(getTargetLayoutPointer(compPointer)))
                const layoutTransformer = conversionDataTransformers.getLayoutTransformer(
                    extensionAPI,
                    compPointer,
                    conversionAlgoConfig
                )
                const transformedLayout = layoutTransformer(layoutToUpdate)

                variantsApi().updateComponentDataConsideringVariants(compPointer, transformedLayout, DATA_TYPES.layout)
            }

            const updateStyle = (compPointer: CompRef, changes: DalJsStore) => {
                if (!changes[ConversionDalNamespaces.style]?.[compPointer.id]) {
                    return
                }

                const styleTransformer = conversionDataTransformers.getStyleTransformer(
                    extensionAPI,
                    compPointer,
                    deepClone(conversionDal.get(getStylePointer(compPointer)))
                )

                const styleItem = styleTransformer()

                if (!styleItem) {
                    return
                }

                if (!builderComponents.isBuilder(components.getComponentType(compPointer))) {
                    return variantsApi().updateComponentDataConsideringVariants(
                        compPointer,
                        styleItem,
                        DATA_TYPES.theme
                    )
                }

                return builderStyle.component.updateCSSStyle(compPointer, styleItem)
            }

            const updateInnerElements = (compPointer: CompRef, changes: DalJsStore) => {
                if (!changes[ConversionDalNamespaces.style]) {
                    return
                }

                const innerElementRootPointer = getInnerElementPointer({id: compPointer.id})

                if (!conversionDal.get(innerElementRootPointer)) {
                    return
                }

                const innerElementIterators = createStructureIterators(conversionDal, innerElementRootPointer)

                innerElementIterators.forEachComponent(
                    (innerElementPointer: Pointer) => {
                        if (innerElementPointer.id === compPointer.id) {
                            return
                        }

                        if (!changes[ConversionDalNamespaces.style]?.[innerElementPointer.id]) {
                            return
                        }

                        const innerElementStyleItem = deepClone(conversionDal.get(getStylePointer(innerElementPointer)))

                        if (!innerElementStyleItem) {
                            return
                        }

                        const styleTransformer = conversionDataTransformers.getStyleTransformer(
                            extensionAPI,
                            compPointer,
                            innerElementStyleItem
                        )

                        const transformedStyleItem = styleTransformer()

                        if (!transformedStyleItem) {
                            return
                        }

                        const {innerElements} = extensionAPI as InnerElementsExtensionAPI
                        const innerElementPath = innerElementPointer.id.split('.').slice(1)

                        innerElements.setInnerElementStyle(
                            compPointer,
                            innerElementPath,
                            transformedStyleItem as Partial<BuilderStyle>
                        )
                    },
                    [ChildrenProperties.innerElements]
                )
            }

            const updateTransformations = (compPointer: CompRef, changes: DalJsStore) => {
                if (!changes[ConversionDalNamespaces.transformations]?.[compPointer.id]) {
                    return
                }

                const transformationsItem = deepClone(conversionDal.get(getTransformationsPointer(compPointer)))
                const {variants} = extensionAPI as VariantsExtensionAPI
                let baseTransformationsItem = variants.getComponentItemConsideringVariants(
                    compPointer,
                    DATA_TYPES.transformations
                )
                if (!baseTransformationsItem) {
                    baseTransformationsItem = dal.schema.createItemAccordingToSchema(
                        'TransformData',
                        DATA_TYPES.transformations
                    )
                }
                const transformationItem = _.merge(baseTransformationsItem, transformationsItem)

                variantsApi().updateComponentDataConsideringVariants(
                    compPointer,
                    transformationItem,
                    DATA_TYPES.transformations
                )
            }

            const writersToExecute = _.over([
                updateResponsiveLayout,
                updateStyle,
                updateInnerElements,
                updateTransformations
            ])
            const changes = conversionDal.getLastSnapshot()!.diff(initialSnapshot)

            structureIterators.forEachComponent(
                pointer => {
                    const compPointerWithVariants = pointers.getPointer(pointer.id, VIEW_MODES.DESKTOP, {
                        variants: conversionAlgoConfig.targetVariants
                    })

                    writersToExecute(compPointerWithVariants, changes)
                },
                [
                    ChildrenProperties.components,
                    ChildrenProperties.hiddenComponents,
                    ChildrenProperties.backgroundComponents
                ]
            )

            updateLayoutSettings()
        }

        const getLastMobileAlgoVersion = (compRef: CompRef): string | null => {
            return (
                variantsApi().getComponentItemConsideringVariants(compRef, DATA_TYPES.layoutSettings)
                    ?.autoResponsivenessSettings?.lastMobileAlgoVersion ?? null
            )
        }

        const getLastGroupingHash = (compRef: CompRef): string | null => {
            return (
                variantsApi().getComponentItemConsideringVariants(compRef, DATA_TYPES.layoutSettings)
                    ?.autoResponsivenessSettings?.lastGroupingsHash ?? null
            )
        }

        const getRequiredOrder = (compRef: CompRef): DomOrderStrategy => {
            return (
                variantsApi().getComponentItemConsideringVariants(compRef, DATA_TYPES.editorsettings)
                    ?.domOrderStrategy ?? DomOrderStrategy.ZShape
            )
        }

        const isComponentLocked = (compRef: CompRef): boolean =>
            variantsApi().getComponentItemConsideringVariants(compRef, DATA_TYPES.layoutSettings)
                ?.autoResponsivenessSettings?.mode === AlgorithmModes.Adaptive

        const getMode = (compRef: CompRef): AlgorithmModes =>
            isComponentLocked(compRef) ? AlgorithmModes.Adaptive : AlgorithmModes.Flush

        const getExecutionSettings = (
            compPointer: CompRef
        ): {
            mode: AlgorithmModes
        } => {
            const mode = getMode(compPointer)
            return {
                mode
            }
        }

        const getTargetBreakpointForComponent = (compRef: CompRef): VariantPointer => {
            const pageMobileBreakpoint = getMobileBreakpoint(
                dal,
                pointers,
                pointers.structure.getPageOfComponent(compRef) as CompRef
            )!
            const componentBreakpoint = getMobileBreakpoint(dal, pointers, compRef)

            return componentBreakpoint ?? pageMobileBreakpoint ?? MOBILE_VARIANT
        }

        const createConversionConfig = (
            containerPointer: CompRef,
            configurationOverrides?: ConversionAlgoConfigOverrides
        ): ConversionAlgoConfig => {
            const defaultTargetBreakpoint = getTargetBreakpointForComponent(containerPointer)

            return _.defaults(configurationOverrides, {
                sourceVariants: containerPointer.variants,
                sourceCanvasSize: DOCUMENT_STAGE_WIDTH,
                targetVariants: [defaultTargetBreakpoint],
                targetCanvasSize: 390,
                ...getExecutionSettings(
                    pointers.components.getComponentVariantsPointer(
                        containerPointer,
                        configurationOverrides?.targetVariants ?? [defaultTargetBreakpoint]
                    ) as CompRef
                ),
                lastMobileAlgoVersion: getLastMobileAlgoVersion(containerPointer),
                MOBILE_ALGO_VERSION,
                lastGroupingHash: getLastGroupingHash(containerPointer),
                requiredOrder: getRequiredOrder(containerPointer),
                rootPointer: containerPointer,
                debug: false
            })
        }

        const runWithContext = async (
            containerPointer: CompRef,
            ctx: MobileAlgoContext,
            conversionAlgoConfig: ConversionAlgoConfig
        ) => {
            const conversionData = prepareConversionData(containerPointer, conversionAlgoConfig)
            const conversionDal = createConversionDal(conversionData)
            const initialSnapshot = conversionDal.getLastSnapshot()!
            const structureIterators = createStructureIterators(conversionDal, getStructurePointer(containerPointer))
            const pageId = getPageId(containerPointer)
            const stageRunner = new StageRunner(stages, readOnlyExtensionAPI, conversionAlgoConfig)

            await stageRunner.run(
                ctx,
                conversionDal,
                initialSnapshot,
                pageId,
                conversionAlgoConfig,
                conversionAlgoConfig.debug ? actionsLogger : undefined
            )

            return {
                conversionDal,
                structureIterators,
                initialSnapshot
            }
        }

        const run = async (compPointer: CompRef, configurationOverrides?: ConversionAlgoConfigOverrides) => {
            const conversionAlgoConfig: ConversionAlgoConfig = createConversionConfig(
                compPointer,
                configurationOverrides
            )
            const ctx = createContext(conversionAlgoConfig.plugins as any)
            const {conversionDal, initialSnapshot, structureIterators} = await runWithContext(
                compPointer,
                ctx,
                conversionAlgoConfig
            )

            writeConversionResult(conversionDal, initialSnapshot!, structureIterators, conversionAlgoConfig, ctx)
        }

        const getComponentsToRun = (pagePointer: CompRef, includeSharedParts = true): CompRef[] => {
            const pageChildren = pointers.structure.getChildren(pagePointer)
            const {components} = extensionAPI as StructureExtensionAPI

            return _.uniqBy(
                pageChildren.reduce((compRefs: Pointer[], child: Pointer) => {
                    const compType = components.getComponentType(child)

                    if (
                        compType === COMP_TYPES.RESPONSIVE_SECTION ||
                        (extensionAPI as LayoutExtensionApi).components.isPinned(child)
                    ) {
                        compRefs.push(child)
                    }

                    const referredComp =
                        includeSharedParts && pointers.structure.getSharedBlockFromInstance(child as CompRef)

                    if (referredComp) {
                        compRefs.push(referredComp)
                    }

                    return compRefs
                }, []) as CompRef[],
                'id'
            )
        }

        const runForPage = async (pagePointer: CompRef) =>
            Promise.all(getComponentsToRun(pagePointer).map(pointer => run(pointer)))

        const runForAllPages = async () => {
            const allPagesIds = pages().getAllPagesIds(false)
            const componentsToRunFromAllPages = _.uniqBy(
                allPagesIds
                    .map(pageId => getComponentsToRun(pointers.structure.getPage(pageId, VIEW_MODES.DESKTOP)))
                    .flat(),
                'id'
            )

            return Promise.all(componentsToRunFromAllPages.map(pointer => run(pointer)))
        }

        const unlockComponent = (sectionPointer: Pointer) => {
            const sectionCompRef = sectionPointer as CompRef

            if (isComponentLocked(sectionCompRef)) {
                const layoutSettings = variantsApi().getComponentItemConsideringVariants(
                    sectionCompRef,
                    DATA_TYPES.layoutSettings
                )

                const autoResponsivenessSettings = {...layoutSettings?.autoResponsivenessSettings}
                delete autoResponsivenessSettings.mode

                variantsApi().updateComponentDataConsideringVariants(
                    sectionCompRef,
                    {
                        ...layoutSettings,
                        autoResponsivenessSettings,
                        type: 'LayoutSettings'
                    },
                    DATA_TYPES.layoutSettings
                )
            }
        }

        const unlockSectionsForPage = (pagePointer: CompRef) => {
            const sections = getComponentsToRun(pagePointer, false)

            sections.forEach(unlockComponent)
        }

        const unlockSectionsForAllPages = () => {
            const allPagesIds = pages().getAllPagesIds(false)
            const componentsToRunFromAllPages = _.uniqBy(
                allPagesIds
                    .map(pageId => getComponentsToRun(pointers.structure.getPage(pageId, VIEW_MODES.DESKTOP)))
                    .flat(),
                'id'
            )

            componentsToRunFromAllPages.forEach(unlockComponent)
        }

        return {
            mobileAlgo: {
                locking: {
                    isComponentLocked,
                    unlockSectionsForAllPages,
                    unlockSectionsForPage
                },
                plugins: {
                    register: registerPlugin,
                    getApis: getPluginApis
                },
                context: {
                    create: createContext,
                    enable: enablePluginsForContext,
                    disable: disablePluginsForContext
                },
                heuristics: {
                    registerHeuristic
                },
                groupings: {
                    getLastGroupingHash
                },
                utils: {
                    getFontSize
                },
                createConversionConfig,
                runWithContext,
                writeConversionResult,
                run,
                runForAllPages,
                runForPage
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {mobileAlgo} = extensionAPI as MobileAlgoExtensionApi

        const wrapAsyncFunction = (fn: MobileAlgoRunFunction) => {
            return async (
                containerPointer: CompRef,
                resolvedFunctions?: ResolvedFunctions,
                config?: ConversionAlgoConfigOverrides
            ) => {
                try {
                    await fn(containerPointer, config)
                    resolvedFunctions?.onSuccess?.()
                } catch (e: any) {
                    logger.captureError(e)
                    resolvedFunctions?.onError?.()
                }
            }
        }
        return {
            mobileAlgo: {
                run: wrapAsyncFunction(mobileAlgo.run),
                runForPage: wrapAsyncFunction(mobileAlgo.runForPage),
                locking: {
                    unlockSectionsForAllPages: mobileAlgo.locking.unlockSectionsForAllPages,
                    unlockSectionsForPage: mobileAlgo.locking.unlockSectionsForPage
                },
                utils: {
                    getFontSize: mobileAlgo.utils.getFontSize
                }
            }
        }
    }

    return {
        name: 'mobileAlgo',
        initialize: async ({extensionAPI}: DmApis) => {
            const mobileAlgoApi = extensionAPI as MobileAlgoExtensionApi
            bootPlugins.forEach(pluginFactory => {
                mobileAlgoApi.mobileAlgo.plugins.register(pluginFactory)
            })
        },
        createExtensionAPI,
        createPublicAPI
    }
}

export {createExtension}
